import { CalendarIcon, SettingsIcon } from "@xola/icons";
import { DatePickerPopover, formatDate, PopoverList } from "@xola/ui-kit";
import { DateFormat, isFuture, now, toLocalDate } from "common";
import dayjs, { Dayjs } from "dayjs";
import React, { useEffect, useState } from "react";
import { PurchaseStatus } from "types";
import shallow from "zustand/shallow";
import { dateFields, usePurchaseFilters } from "../../hooks/usePurchaseFilters";
import { useVisibility } from "../../hooks/useVisibility";
import { DateType, PurchaseFilter, usePurchaseFilterStore } from "../../stores/purchaseFilterStore";
import { ResourceSelectionState } from "../../types/Dashboard";
import { FilterMenuButton } from "../Dashboard/Filters/FilterMenuButton";
import { ListFilter } from "../Dashboard/Filters/ListFilter";
import { FilterButton } from "./PurchaseFilterButton";

interface DatePickerDate {
    from: Date | null;
    to: Date | null;
}

interface PurchaseFilterListProps {
    isOpen: boolean;
}

export const PurchaseFilterList = ({ isOpen }: PurchaseFilterListProps) => {
    const [to, setTo] = usePurchaseFilterStore((state) => [state.to, state.setTo], shallow);
    const [from, setFrom] = usePurchaseFilterStore((state) => [state.from, state.setFrom], shallow);
    const [filters, setFilters] = usePurchaseFilterStore((state) => [state.filters, state.setFilters], shallow);

    const [toDate, setToDate] = useState(to ? toLocalDate(to).toDate() : null);
    const [fromDate, setFromDate] = useState(from ? toLocalDate(from).toDate() : null);

    const [value, setValue] = useState({ from: from?.toDate() ?? null, to: to?.toDate() ?? null });

    const [showPopover, { toggle: togglePopover }] = useVisibility(false);

    const FiltersList = usePurchaseFilters();

    const isDateDisabled = (date: Date) => {
        const from = now(value?.from ?? fromDate);
        const fromLocalDate = toLocalDate(from.endOf("day")).toDate();

        const diffInDays = dayjs(date).startOf("day").diff(fromLocalDate, "day", true);
        const diffInYears = diffInDays / 365.25; // More precise year calculation

        return diffInYears > 1 ? true : filters.date === DateType.PURCHASE_DATE ? isFuture(date, "day") : false;
    };

    useEffect(() => {
        if (from && !from.isSame(fromDate)) {
            setFromDate(toLocalDate(from).toDate());
        } else if (!from) {
            setFromDate(null);
        }

        if (to && !to.isSame(toDate)) {
            setToDate(toLocalDate(to).toDate());
        } else if (!to) {
            setToDate(null);
        }

        if (!to && !from) {
            setValue({ from: null, to: null });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [from, to, setToDate, setFromDate, setValue]);

    useEffect(() => {
        setValue({
            from: from ? toLocalDate(from).toDate() : null,
            to: to ? toLocalDate(to).endOf("day").toDate() : null,
        });
    }, [from, to]);

    const handleDateChange = (dateValue: DatePickerDate, dateType?: DateType) => {
        const newDateType = dateType ?? filters.date;
        const newDateValue = handleDateUpdate(newDateType, dateValue); // Get the updated date value based on the filter type
        const { from: newFrom, to: newTo } = newDateValue;
        setValue(newDateValue);

        if (newFrom && newTo) {
            setFrom(now(newFrom).startOf("day"));
            setTo(now(newTo).startOf("day"));
        }
    };

    const handleDateUpdate = (dateType: DateType, dateValue: DatePickerDate) => {
        if (dateType !== DateType.PURCHASE_DATE) {
            return dateValue;
        }

        if (dateValue.from && dateValue.to && (isFuture(dateValue.from) || isFuture(dateValue.to))) {
            // When changing from arrival date to purchase date, you need to check if the dates are in the future
            console.debug("Purchase date filter is in the future, resetting to today");
            const newFrom = isFuture(dateValue.from) ? now().startOf("day").toDate() : dateValue.from;
            const newTo = isFuture(dateValue.to) ? now().toDate() : dateValue.to;
            return { from: newFrom, to: newTo };
        }

        return dateValue;
    };

    if (!isOpen) {
        return null;
    }

    return (
        <div className="flex items-center justify-start space-x-1 border-b border-gray-light px-10 py-2 xl:space-x-2">
            <PopoverList offset={[0, 10]} visible={showPopover} onClickOutside={togglePopover}>
                <FilterMenuButton
                    isDropdown
                    className="h-10 w-40 rounded-r-none border-r-0"
                    text={dateFields.find((item) => item.id === filters.date)?.name}
                    onClick={togglePopover}
                />
                {dateFields.map((item) => (
                    <PopoverList.Item
                        key={item.id}
                        name={item.name}
                        isActive={item.id === filters.date}
                        onClickItem={() => {
                            togglePopover();
                            setFilters({ date: item.id });
                            // When changing from arrival date to purchase date, you need to check if the dates are in the future
                            handleDateChange(value, item.id);
                        }}
                    >
                        {item.name}
                    </PopoverList.Item>
                ))}
            </PopoverList>

            <DatePickerPopover
                shouldShowYearPicker
                shouldShowRelativeRanges
                disabledDays={isDateDisabled}
                variant="range"
                value={value}
                popoverProps={{ placement: "bottom-end", distance: 8 }}
                isFutureDatesAllowed={filters.date === DateType.ARRIVAL_DATE}
                onChange={handleDateChange}
            >
                <div className="-ml-4 flex h-10 cursor-pointer items-center space-x-2 rounded rounded-l-none border border-l border-gray-light p-3">
                    <span>
                        <CalendarIcon size="medium" />
                    </span>
                    <span>
                        {from || to
                            ? `${value.from ? formatDate(value.from, DateFormat.DATE) : "Pending"} - ${
                                  value.to ? formatDate(value.to, DateFormat.DATE) : "Pending"
                              }`
                            : "All Days"}
                    </span>
                </div>
            </DatePickerPopover>

            {FiltersList.map((filter) => (
                <ListFilter
                    key={filter.label}
                    disableUnassignedOnSelection
                    label={filter.label}
                    title={"Filter " + filter.label}
                    target={FilterButton}
                    fetch={filter.fetch}
                    value={filter.value}
                    localize={filter.localize}
                    unassignedLabel={filter.value.showUnassigned ? "purchases" : ""}
                    show={filter.show}
                    onChange={filter.onChange}
                />
            ))}
        </div>
    );
};

interface AdvancedSearchProps {
    onClick: () => void;
    isPurchaseFilterOpen: boolean;
}

export const AdvancedSearch = ({ onClick, isPurchaseFilterOpen }: AdvancedSearchProps) => {
    return (
        <span className="flex cursor-pointer items-center font-bold underline underline-offset-2" onClick={onClick}>
            <SettingsIcon className="top-0.25 mr-1.5" />
            {isPurchaseFilterOpen && "Close "}Advanced Search
        </span>
    );
};

export const getPurchaseFilterParams = (from: Dayjs | null, to: Dayjs | null, filters: PurchaseFilter) => {
    const params: any = {
        status: PurchaseStatus.COMMITTED,
        limit: 20,
    };

    if (from && to) {
        if (filters.date === DateType.ARRIVAL_DATE) {
            params[filters.date + "[range]"] = `${formatDate(from)},${formatDate(to?.endOf("day"))}`;
        }

        if (filters.date === DateType.PURCHASE_DATE) {
            params[filters.date + "[range]"] = `${from?.toISOString()},${to?.endOf("day").toISOString()}`;
        }
    }

    if (isPurchaseFilterApplied(filters.type)) {
        params["items.object[in]"] = filters.type.selected.join(",");
    }

    if (isPurchaseFilterApplied(filters.product)) {
        params["items.product[in]"] = filters.product.selected.join(",");
    }

    if (isPurchaseFilterApplied(filters.status)) {
        params["items.status[in]"] = filters.status.selected.join(",");
    }

    if (isPurchaseFilterApplied(filters.balance) && isPurchaseFilterApplied(filters.balance)) {
        Object.assign(params, getBalanceFilterParams(filters.balance.selected));
    }

    if (isPurchaseFilterApplied(filters.source)) {
        params["source[in]"] = filters.source.selected.join(",");
    }

    if (isPurchaseFilterApplied(filters.dispute)) {
        if (filters.dispute.all) {
            params["lineItems.payment.dispute.status[exists]"] = true;
        } else if (filters.dispute.selected.length > 0) {
            params["lineItems.payment.dispute.status[in]"] = filters.dispute.selected.join(",");
        }

        if (filters.dispute.showUnassigned) {
            params["lineItems.payment.dispute.status[exists]"] = false;
        }
    }

    if (isPurchaseFilterApplied(filters.affiliate)) {
        if (filters.affiliate.all) {
            params["partner[exists]"] = true;
        } else if (filters.affiliate.selected.length > 0) {
            params["partner[in]"] = filters.affiliate.selected.join(",");
        }

        if (filters.affiliate.showUnassigned) {
            params["partner[exists]"] = false;
        }
    }

    return params;
};

export const isPurchaseFilterApplied = (filter: ResourceSelectionState) => {
    if (filter.showUnassigned === false) {
        return filter.selected.length > 0 || filter.all;
    }

    if (filter.showUnassigned === true) {
        return filter.selected.length === 0 && !filter.all;
    }

    return filter.selected.length > 0;
};

const getBalanceFilterParams = (selected: string[]) => {
    const params: Record<string, number> = {};
    const [hasDue, hasRefund, hasPaid] = [
        selected.includes("due"),
        selected.includes("refund"),
        selected.includes("paid"),
    ];

    if (selected.length === 1) {
        if (hasDue) return { "balance[gt]": 0 };
        if (hasRefund) return { "balance[lt]": 0 };
        if (hasPaid) return { "balance[equals]": 0 };
    }

    if (selected.length === 2) {
        if (hasDue && hasRefund) return { "balance[notEqual]": 0 };
        if (hasDue && hasPaid) return { "balance[gte]": 0 };
        if (hasRefund && hasPaid) return { "balance[lte]": 0 };
    }

    return params;
};
